import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Users, Upload, UserPlus, Search, Loader2, ArrowRight, BarChart3, Trash2, Plus, Globe,
  Calendar, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import LeadScorePill from '@/components/leads/LeadScorePill';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import RaffleManager from '@/components/raffles/RaffleManager';
import { Gift } from 'lucide-react';
import { leadsAPI, agentAPI, leadLandingPagesAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isKRRole, formatPhone, formatPhoneInput } from '@/lib/utils';
import { GoogleCalendarConnectPrompt, ConnectGCalChip } from '@/components/integrations/GoogleCalendarConnectPrompt';
import LeadSourcePicker from '@/components/leads/LeadSourcePicker';
import AddressAutocomplete from '@/components/common/AddressAutocomplete';
import { flashAutofilled } from '@/lib/flashAutofilled';

const STATUS_META = {
  new: { label: 'New', color: 'bg-slate-100 text-slate-700 border-slate-300' },
  attempting_contact: { label: 'Attempting', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  contacted: { label: 'Contacted', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  quoted: { label: 'Quoted', color: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
  appointment: { label: 'Appointment', color: 'bg-purple-100 text-purple-800 border-purple-300' },
  closed_won: { label: 'Closed Won', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  not_interested: { label: 'Not Interested', color: 'bg-rose-100 text-rose-700 border-rose-300' },
};

export default function LeadsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isKR = isKRRole(user);
  const [tab, setTab] = useState('leads');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [filters, setFilters] = useState({ q: '', status: 'all', assigned: 'all', language: 'all', source: 'all', landing_page: 'all' });
  const [landingPages, setLandingPages] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [showAssign, setShowAssign] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [agents, setAgents] = useState([]);
  const [deleting, setDeleting] = useState(false);

  // Phase 27.53 — Marketing ROI deep-link. When landing here via
  // "View leads" from an Event/Retail detail dialog, honour the URL
  // filters so the list only shows leads captured for that campaign.
  const eventRequestId = searchParams.get('event_request_id') || '';
  const retailAssignmentId = searchParams.get('retail_assignment_id') || '';
  const retailProgramRequestId = searchParams.get('retail_program_request_id') || '';
  const contextChip = eventRequestId
    ? { kind: 'event', label: 'Filtered by Event Request', value: eventRequestId }
    : retailProgramRequestId
    ? { kind: 'retail', label: 'Filtered by Retail Program', value: retailProgramRequestId }
    : retailAssignmentId
    ? { kind: 'retail_slot', label: 'Filtered by Retail Slot', value: retailAssignmentId }
    : null;

  const clearContextFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('event_request_id');
    next.delete('retail_assignment_id');
    next.delete('retail_program_request_id');
    setSearchParams(next, { replace: true });
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const msg = ids.length === 1
      ? 'Delete this lead? This action cannot be undone — related follow-ups and activity will also be removed.'
      : `Delete ${ids.length} leads? This action cannot be undone — related follow-ups and activity will also be removed.`;
    if (!window.confirm(msg)) return;
    setDeleting(true);
    try {
      const res = await leadsAPI.bulkDelete(ids);
      const { deleted, skipped } = res.data;
      toast.success(`Deleted ${deleted} lead${deleted === 1 ? '' : 's'}${skipped ? ` (${skipped} skipped — not yours)` : ''}`);
      setSelected(new Set());
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete leads');
    } finally {
      setDeleting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.q) params.q = filters.q;
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.assigned !== 'all') params.assigned = filters.assigned;
      if (filters.source !== 'all') params.source = filters.source;
      if (filters.landing_page !== 'all') params.landing_page_id = filters.landing_page;
      if (eventRequestId) params.event_request_id = eventRequestId;
      if (retailAssignmentId) params.retail_assignment_id = retailAssignmentId;
      if (retailProgramRequestId) params.retail_program_request_id = retailProgramRequestId;
      params.limit = pageSize;
      params.skip = (page - 1) * pageSize;
      const res = await leadsAPI.list(params);
      setLeads(res.data.leads || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [filters, eventRequestId, retailAssignmentId, retailProgramRequestId, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  // Reset to the first page whenever the filters or page size change.
  useEffect(() => { setPage(1); }, [filters, eventRequestId, retailAssignmentId, retailProgramRequestId, pageSize]);

  useEffect(() => {
    leadLandingPagesAPI.options()
      .then((r) => setLandingPages(r.data.items || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isKR) return;
    agentAPI.list().then((r) => setAgents(r.data || [])).catch(() => {});
  }, [isKR]);

  const toggleAll = () => {
    if (selected.size === visibleLeads.length) setSelected(new Set());
    else setSelected(new Set(visibleLeads.map((l) => l.lead_id)));
  };

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const summary = useMemo(() => {
    const counts = Object.keys(STATUS_META).reduce((acc, k) => ({ ...acc, [k]: 0 }), {});
    leads.forEach((l) => { counts[l.status] = (counts[l.status] || 0) + 1; });
    return counts;
  }, [leads]);

  // Client-side language filter. `preferred_language` is only present
  // on leads captured through the landing page — other leads default
  // to English for filter purposes so the picker stays predictable.
  const visibleLeads = useMemo(() => {
    if (filters.language === 'all') return leads;
    return leads.filter((l) => (l.preferred_language || 'en') === filters.language);
  }, [leads, filters.language]);

  return (
    <div className="page-content space-y-6" data-testid="leads-page">
      <GoogleCalendarConnectPrompt context="leads" />
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900">Leads</h1>
          <p className="text-sm text-slate-500">
            {isKR ? 'Import, assign and track leads across the agency.' : 'Your assigned leads and progress.'}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <ConnectGCalChip />
          <Link to="/leads/reports">
            <Button variant="outline" data-testid="leads-reports-btn">
              <BarChart3 size={16} className="mr-2" /> Reports
            </Button>
          </Link>
          <Link to="/leads/appointments">
            <Button variant="outline" data-testid="leads-appointments-btn">
              <Calendar size={16} className="mr-2" /> Appointments
            </Button>
          </Link>
          {isKR && (
            <Link to="/leads/landing-pages">
              <Button variant="outline" data-testid="leads-landing-pages-btn">
                <Globe size={16} className="mr-2" /> Landing Pages
              </Button>
            </Link>
          )}
          {/* Import + New Lead available to KR admins and agents alike.
              Agents' additions are auto-assigned to themselves. */}
          <Link to="/leads/import">
            <Button variant="outline" data-testid="leads-import-btn">
              <Upload size={16} className="mr-2" /> Import
            </Button>
          </Link>
          <Button onClick={() => setShowNew(true)} data-testid="leads-new-btn">
            <Plus size={16} className="mr-2" /> New Lead
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="flex-wrap h-auto" data-testid="leads-tabs">
          <TabsTrigger value="leads" data-testid="leads-tab-all">Leads</TabsTrigger>
          <TabsTrigger value="raffles" data-testid="leads-tab-raffles">
            <Gift size={14} className="mr-1.5" /> Raffles
          </TabsTrigger>
        </TabsList>

        <TabsContent value="leads" className="space-y-6 mt-4">
      {/* status counters */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {Object.entries(STATUS_META).map(([k, m]) => (
          <div key={k} className={`rounded-md border px-3 py-2 ${m.color}`}>
            <p className="text-[10px] uppercase tracking-wide opacity-80">{m.label}</p>
            <p className="text-xl font-semibold">{summary[k] || 0}</p>
          </div>
        ))}
      </div>

      {/* Phase 27.53 — Marketing ROI context chip (deep-link filter). */}
      {contextChip && (
        <div
          className="flex items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm"
          data-testid="leads-context-chip"
        >
          <div className="flex items-center gap-2 text-amber-900">
            <Calendar size={14} />
            <span className="font-semibold">{contextChip.label}</span>
            <span className="text-xs text-amber-700">{leads.length} leads</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-amber-800 hover:bg-amber-100"
            onClick={clearContextFilter}
            data-testid="leads-context-chip-clear"
          >
            Clear filter
          </Button>
        </div>
      )}

      {/* filters */}
      <Card>
        <CardContent className="pt-4 flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[240px]">
            <Label className="text-xs">Search</Label>
            <div className="relative">
              <Search size={14} className="absolute left-2 top-2.5 text-slate-400" />
              <Input
                className="pl-7"
                placeholder="Name / email / phone"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                data-testid="leads-search"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger className="w-[180px]" data-testid="leads-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {Object.entries(STATUS_META).map(([k, m]) => (
                  <SelectItem key={k} value={k}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {isKR && (
            <div>
              <Label className="text-xs">Assignment</Label>
              <Select value={filters.assigned} onValueChange={(v) => setFilters({ ...filters, assigned: v })}>
                <SelectTrigger className="w-[160px]" data-testid="leads-assignment-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="true">Assigned</SelectItem>
                  <SelectItem value="false">Unassigned</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs">Language</Label>
            <Select value={filters.language} onValueChange={(v) => setFilters({ ...filters, language: v })}>
              <SelectTrigger className="w-[140px]" data-testid="leads-language-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All languages</SelectItem>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="es">Español</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Source</Label>
            <Select value={filters.source} onValueChange={(v) => setFilters({ ...filters, source: v })}>
              <SelectTrigger className="w-[170px]" data-testid="leads-source-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sources</SelectItem>
                <SelectItem value="kora_chatbot">Kora chatbot</SelectItem>
                <SelectItem value="landing_page">Landing page</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="import">Import</SelectItem>
                <SelectItem value="referral">Referral</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {landingPages.length > 0 && (
            <div>
              <Label className="text-xs">Referral page</Label>
              <Select value={filters.landing_page} onValueChange={(v) => setFilters({ ...filters, landing_page: v })}>
                <SelectTrigger className="w-[190px]" data-testid="leads-landing-page-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All referral pages</SelectItem>
                  {landingPages.map((lp) => (
                    <SelectItem key={lp.landing_page_id} value={lp.landing_page_id}>{lp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {isKR && selected.size > 0 && (
            <Button onClick={() => setShowAssign(true)} data-testid="leads-bulk-assign-btn">
              <UserPlus size={14} className="mr-2" /> Assign {selected.size}
            </Button>
          )}
          {selected.size > 0 && (
            <Button
              variant="outline"
              onClick={handleBulkDelete}
              disabled={deleting}
              data-testid="leads-bulk-delete-btn"
              className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
            >
              {deleting ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Trash2 size={14} className="mr-2" />}
              Delete {selected.size}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500"><Loader2 className="animate-spin inline mr-2" size={16} />Loading…</div>
          ) : visibleLeads.length === 0 ? (
            <div className="p-8 text-center text-slate-500" data-testid="leads-empty">
              <Users size={32} className="mx-auto mb-2 text-slate-300" />
              {filters.language !== 'all'
                ? `No ${filters.language === 'es' ? 'Spanish' : 'English'}-speaking leads match the current filters.`
                : 'No leads to show. Try importing a CSV/Excel or creating one manually.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2">
                      <Checkbox
                        checked={selected.size === visibleLeads.length && visibleLeads.length > 0}
                        onCheckedChange={toggleAll}
                        data-testid="leads-select-all"
                      />
                    </th>
                    <th className="text-left px-3 py-2 font-medium">Name</th>
                    <th className="text-left px-3 py-2 font-medium">Contact</th>
                    <th className="text-left px-3 py-2 font-medium">State</th>
                    <th className="text-left px-3 py-2 font-medium">Product</th>
                    <th className="text-left px-3 py-2 font-medium">Assigned</th>
                    <th className="text-left px-3 py-2 font-medium">Status</th>
                    <th className="text-left px-3 py-2 font-medium">AI</th>
                    <th className="w-6"></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleLeads.map((l) => {
                    const meta = STATUS_META[l.status] || STATUS_META.new;
                    return (
                      <tr key={l.lead_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                        <td className="px-3 py-2">
                          <Checkbox
                            checked={selected.has(l.lead_id)}
                            onCheckedChange={() => toggleOne(l.lead_id)}
                            data-testid={`lead-select-${l.lead_id}`}
                          />
                        </td>
                        <td className="px-3 py-2 cursor-pointer" onClick={() => navigate(`/leads/${l.lead_id}`)}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-slate-900">{l.first_name} {l.last_name || ''}</p>
                            {l.preferred_language && (
                              <span
                                className={`inline-flex items-center rounded-full border text-[10px] font-semibold px-1.5 py-0.5 uppercase tracking-wide ${
                                  l.preferred_language === 'es'
                                    ? 'bg-rose-50 border-rose-200 text-rose-700'
                                    : 'bg-sky-50 border-sky-200 text-sky-700'
                                }`}
                                title={l.preferred_language === 'es' ? 'Prefiere español' : 'Prefers English'}
                                data-testid={`lead-lang-chip-${l.lead_id}`}
                              >
                                {l.preferred_language === 'es' ? 'ES' : 'EN'}
                              </span>
                            )}
                            {l.has_duplicate_alert && (
                              <span
                                className="inline-flex items-center gap-1 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-[10px] font-semibold px-1.5 py-0.5"
                                title="Possible duplicate in the CRM"
                                data-testid={`lead-duplicate-chip-${l.lead_id}`}
                              >
                                ⚠ Duplicate
                              </span>
                            )}
                            {l.source === 'kora_chatbot' && (
                              <span
                                className="inline-flex items-center gap-1 rounded-full bg-violet-100 border border-violet-300 text-violet-800 text-[10px] font-semibold px-1.5 py-0.5"
                                title="Created from a Kora chatbot conversation"
                                data-testid={`lead-source-kora-${l.lead_id}`}
                              >
                                ✨ Kora
                              </span>
                            )}
                            {l.landing_page_name && (
                              <span
                                className="inline-flex items-center gap-1 rounded-full bg-teal-50 border border-teal-300 text-teal-800 text-[10px] font-semibold px-1.5 py-0.5 max-w-[160px] truncate"
                                title={`Referred via: ${l.landing_page_name}`}
                                data-testid={`lead-referral-chip-${l.lead_id}`}
                              >
                                <Globe size={10} className="shrink-0" /> {l.landing_page_name}
                              </span>
                            )}
                          </div>
                          {l.age && <p className="text-xs text-slate-500">Age {l.age}</p>}
                        </td>
                        <td className="px-3 py-2 text-slate-700">
                          {l.phone && <p className="text-xs">{formatPhone(l.phone)}</p>}
                          {l.email && <p className="text-xs text-slate-500">{l.email}</p>}
                        </td>
                        <td className="px-3 py-2 text-slate-700">{l.state || '-'}</td>
                        <td className="px-3 py-2 text-slate-700">{l.product_of_interest || '-'}</td>
                        <td className="px-3 py-2 text-slate-700">{l.assigned_agent_name || <span className="text-slate-400 italic">Unassigned</span>}</td>
                        <td className="px-3 py-2">
                          <Badge className={`${meta.color} border font-normal`}>{meta.label}</Badge>
                        </td>
                        <td className="px-3 py-2">
                          <LeadScorePill score={l.ai_score} showLabel />
                        </td>
                        <td className="px-2 py-2 text-slate-400 cursor-pointer" onClick={() => navigate(`/leads/${l.lead_id}`)}>
                          <ArrowRight size={14} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!loading && total > 0 && (
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 text-sm flex-wrap" data-testid="leads-pagination">
              <div className="flex items-center gap-3">
                <span className="text-slate-500">
                  Showing <b>{(page - 1) * pageSize + 1}</b>–<b>{Math.min(page * pageSize, total)}</b> of <b>{total}</b> leads
                </span>
                <div className="flex items-center gap-1.5">
                  <Label className="text-xs text-slate-500 whitespace-nowrap">Per page</Label>
                  <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                    <SelectTrigger className="h-8 w-[72px]" data-testid="leads-page-size"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline" size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  data-testid="leads-prev-page"
                >
                  <ChevronLeft size={14} className="mr-1" /> Previous
                </Button>
                <span className="text-slate-600 text-xs whitespace-nowrap">
                  Page {page} of {Math.max(1, Math.ceil(total / pageSize))}
                </span>
                <Button
                  variant="outline" size="sm"
                  disabled={page >= Math.ceil(total / pageSize)}
                  onClick={() => setPage((p) => p + 1)}
                  data-testid="leads-next-page"
                >
                  Next <ChevronRight size={14} className="ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

        </TabsContent>

        <TabsContent value="raffles" className="mt-4">
          <RaffleManager />
        </TabsContent>
      </Tabs>

      {/* Bulk assign dialog */}
      <BulkAssignDialog
        open={showAssign}
        onOpenChange={setShowAssign}
        agents={agents}
        leadIds={Array.from(selected)}
        onDone={() => { setSelected(new Set()); load(); }}
      />

      {/* New lead dialog */}
      <NewLeadDialog open={showNew} onOpenChange={setShowNew} onDone={load} />
    </div>
  );
}

function BulkAssignDialog({ open, onOpenChange, agents, leadIds, onDone }) {
  const [agentNpn, setAgentNpn] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!agentNpn) { toast.error('Pick an agent'); return; }
    setSaving(true);
    try {
      const res = await leadsAPI.assign(leadIds, agentNpn);
      toast.success(`${res.data.assigned} lead(s) assigned`);
      onOpenChange(false);
      onDone();
    } catch { toast.error('Assign failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Assign {leadIds.length} lead(s)</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <Label>Agent</Label>
          <Select value={agentNpn} onValueChange={setAgentNpn}>
            <SelectTrigger data-testid="assign-agent-select"><SelectValue placeholder="Pick an agent" /></SelectTrigger>
            <SelectContent>
              {agents.map((a) => (
                <SelectItem key={a.npn} value={a.npn}>
                  {a.name || `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.email} ({a.npn})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="assign-submit">
            {saving ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
            Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewLeadDialog({ open, onOpenChange, onDone }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.first_name?.trim()) { toast.error('First name is required'); return; }
    setSaving(true);
    try {
      await leadsAPI.create(form);
      toast.success('Lead created');
      onOpenChange(false);
      setForm({});
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Create failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New Lead</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div><Label>First name *</Label><Input value={form.first_name || ''} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="new-lead-firstname" /></div>
          <div><Label>Last name</Label><Input value={form.last_name || ''} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
          <div>
            <Label>Gender</Label>
            <Select value={form.gender || ''} onValueChange={(v) => setForm({ ...form, gender: v })}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="F">F</SelectItem>
                <SelectItem value="M">M</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Date of birth</Label><Input type="date" value={form.date_of_birth || ''} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} /></div>
          <div><Label>Email</Label><Input value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label>Phone</Label><Input value={formatPhoneInput(form.phone || '')} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <div className="col-span-2">
            <Label>Address</Label>
            <AddressAutocomplete
              value={form.address || ''}
              onChange={(v) => setForm((p) => ({ ...p, address: v }))}
              onPlaceSelected={(parsed) => {
                setForm((p) => ({
                  ...p,
                  address: parsed.street || parsed.formatted,
                  state: parsed.state || p.state,
                  county: parsed.county || p.county,
                }));
                flashAutofilled(['new-lead-state', 'new-lead-county']);
              }}
              testId="new-lead-address"
            />
          </div>
          <div><Label>State</Label><Input value={form.state || ''} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="new-lead-state" /></div>
          <div><Label>County</Label><Input value={form.county || ''} onChange={(e) => setForm({ ...form, county: e.target.value })} data-testid="new-lead-county" /></div>
          <div className="col-span-2"><Label>Product of interest</Label><Input value={form.product_of_interest || ''} onChange={(e) => setForm({ ...form, product_of_interest: e.target.value })} /></div>
          <div>
            <Label>Preferred language</Label>
            <Select value={form.preferred_language || 'en'} onValueChange={(v) => setForm({ ...form, preferred_language: v })}>
              <SelectTrigger data-testid="new-lead-language"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="es">Español</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Medicare ID</Label><Input value={form.medicare_id || ''} onChange={(e) => setForm({ ...form, medicare_id: e.target.value })} placeholder="MBI" data-testid="new-lead-medicare-id" /></div>
          <div className="col-span-2 pt-1 border-t border-slate-100 mt-1">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mt-2 mb-1">Current Plan / Coverage</p>
          </div>
          <div><Label>Carrier</Label><Input value={form.current_plan_carrier || ''} onChange={(e) => setForm({ ...form, current_plan_carrier: e.target.value })} data-testid="new-lead-current-carrier" /></div>
          <div><Label>Plan name</Label><Input value={form.current_plan_name || ''} onChange={(e) => setForm({ ...form, current_plan_name: e.target.value })} data-testid="new-lead-current-plan" /></div>
          <div className="col-span-2">
            <LeadSourcePicker
              source={form.source}
              eventRequestId={form.event_request_id}
              retailAssignmentId={form.retail_assignment_id}
              onChange={({ source, event_request_id, retail_assignment_id }) => setForm((p) => ({
                ...p, source, event_request_id, retail_assignment_id,
              }))}
              testId="new-lead-source"
            />
          </div>
          <div className="col-span-2"><Label>Notes</Label><Input value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="new-lead-submit">
            {saving ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
            Create Lead
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
